import { BackendError, Service } from '@genial/services';
import { FixedFeeEnumsFeeBasedContractListSortFieldEnum } from '@genial/services/dist/models';
import { selectorFamily } from 'recoil';

import { TableHeadItemOrderBy } from '../../components/TableHead';
import { FiltersToApplyProps } from '../../hooks/useFilterTransformer';
import { blankMetaResponse } from '../../utils/blankMetaResponse';
import { transformSortItem } from '../../utils/transformSortItem';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';

import { fixedFeeScreenName } from './constants';
import {
  normalizeFixedFeeCustomerNetWorth,
  normalizeFixedFeeCustomerTermReplacementStatus,
  normalizeFixedFeeNewContractTerm,
  normalizeFixedFeeTermHistory,
  normalizeFixedFeeTermDetails,
  normalizeFixedFeeTerms,
} from './normalize';
import {
  FixedFeeCustomerNetWorthDataState,
  FixedFeeNewContractTermDataState,
  FixedFeeTermHistoryDataState,
  FixedFeeTermDetailsDataState,
  FixedFeeTermsDataState,
  FixedFeeCustomerTermReplacementStatus,
  FixedFeeAvailableConfigsDataState,
} from './types';

export const fixedFeeTermsQuery = selectorFamily<
  FixedFeeTermsDataState,
  {
    page: number;
    filtersToApply: FiltersToApplyProps;
    searchTerm?: string;
    appliedOrderBy?: TableHeadItemOrderBy;
  }
>({
  key: 'FixedFee/TermsQuery',
  get:
    ({ filtersToApply, appliedOrderBy, searchTerm, page }) =>
    async ({ get }) => {
      try {
        const sortItems = transformSortItem(appliedOrderBy);
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);

        const { data, meta } = await Service.FixedFee.V1.getAgreementFeeBasedContractFilter({
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          screenName: fixedFeeScreenName,
          limit: 10,
          ...filtersToApply,
          sortBy: sortItems?.sort as FixedFeeEnumsFeeBasedContractListSortFieldEnum,
          sortDescending: sortItems?.sortDirection
            ? sortItems?.sortDirection === 'desc'
            : undefined,
          page,
          searchTerm: searchTerm || undefined,
        });

        return { data: normalizeFixedFeeTerms(data), meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const fixedFeeTermDetailsQuery = selectorFamily<
  FixedFeeTermDetailsDataState,
  {
    feeBasedContractId: number;
  }
>({
  key: 'FixedFee/TermDetailsQuery',
  get:
    ({ feeBasedContractId }) =>
    async () => {
      const { data } = await Service.FixedFee.V1.getAgreementByFeeBasedContractId({
        screenName: fixedFeeScreenName,
        feeBasedContractId,
      });

      return {
        data: normalizeFixedFeeTermDetails(data),
      };
    },
});

export const FixedFeeAvailableConfigsQuery = selectorFamily<
  FixedFeeAvailableConfigsDataState,
  {
    advisorId: number;
    partnerId: number;
  }
>({
  key: 'FixedFee/AvailableRateRangesQuery',
  get:
    ({ advisorId, partnerId }) =>
    async () => {
      const { data } = await Service.FixedFee.V1.getAgreementFeeBasedContractConfigurationFilter({
        screenName: fixedFeeScreenName,
        advisorId,
        partnerId,
      });

      return {
        data: {
          ableToExcludeEquity: data.canExcludeRv,
          minRate: data.minAnnualPercentage,
          maxRate: data.maxAnnualPercentage,
        },
      };
    },
});

export const fixedFeeCustomerNetWorthQuery = selectorFamily<
  FixedFeeCustomerNetWorthDataState,
  {
    accountNumber: number;
    excludeEquity: boolean;
  }
>({
  key: 'FixedFee/CustomerNetWorthQuery',
  get:
    ({ accountNumber, excludeEquity }) =>
    async () => {
      try {
        const { data } = await Service.FixedFee.V1.getConsolidatedPosition({
          screenName: fixedFeeScreenName,
          accountNumber,
          excludedProducts: excludeEquity ? ['equity'] : undefined,
        });

        return {
          data: normalizeFixedFeeCustomerNetWorth(data),
        };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: undefined };
        }

        throw error;
      }
    },
});

export const fixedFeeCustomerTermReplacementStatusQuery = selectorFamily<
  FixedFeeCustomerTermReplacementStatus,
  {
    accountNumber: number;
    positionTotalValueInvested: number;
    feePercentage: number;
  }
>({
  key: 'FixedFee/CustomerTermReplacementStatusQuery',
  get:
    ({ accountNumber, feePercentage, positionTotalValueInvested }) =>
    async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data } = await Service.FixedFee.V1.postAgreementFeeBasedContractValidate({
        screenName: fixedFeeScreenName,
        validateFixedFeeAgreementRequest: {
          accountNumber,
          positionTotalValueInvested,
          feePercentage,
          advisorId: advisorSelected,
          partnerId: partnerSelected || 0,
          validateOptions: { validateCreateAgreement: true, validatePosition: true },
        },
      });

      return normalizeFixedFeeCustomerTermReplacementStatus(data);
    },
});

export const fixedFeeNewContractTermQuery = selectorFamily<
  FixedFeeNewContractTermDataState,
  {
    accountNumber: number;
    feePercentage: number;
    excludeEquity: boolean;
  }
>({
  key: 'FixedFee/NewContractTermQuery',
  get:
    ({ accountNumber, feePercentage, excludeEquity }) =>
    async () => {
      const { data } = await Service.FixedFee.V1.getAgreementFeeBasedContractTemplateFilter({
        screenName: fixedFeeScreenName,
        accountNumber,
        feePercentage,
        excludedProducts: excludeEquity ? ['equity'] : undefined,
      });

      return {
        data: normalizeFixedFeeNewContractTerm(data),
      };
    },
});

export const fixedFeeTermHistoryQuery = selectorFamily<
  FixedFeeTermHistoryDataState,
  {
    termId: string;
  }
>({
  key: 'FixedFee/TermHistoryQuery',
  get:
    ({ termId: _termId }) =>
    async () => {
      await new Promise((resolve) => setTimeout(resolve, 1200));

      return {
        data: normalizeFixedFeeTermHistory([]),
      };
    },
});
